/**
 * NewAnalysisPage Component
 * Page for creating a new analysis with hero section and CTA
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { DashboardHeader } from '@/components/layout/DashboardHeader';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AnalysisDialog, type AnalysisFormData } from '@/components/analysis/AnalysisDialog';
import { InsufficientCreditsDialog } from '@/components/credits/InsufficientCreditsDialog';
import { BuyCreditsDialog } from '@/components/credits/BuyCreditsDialog';
import { requestsService } from '@/services/api/requests';
import { useAuthStore } from '@/stores/authStore';
import newAnalysisHero from '@/assets/new_analysis.jpg';

export default function NewAnalysisPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [isInsufficientCreditsOpen, setIsInsufficientCreditsOpen] = useState(false);
  const [isBuyCreditsOpen, setIsBuyCreditsOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const totalCredits = useAuthStore((state) => state.user?.credits ?? 0);

  // Auto-open wizard when URL has openWizard param
  useEffect(() => {
    if (searchParams.get('openWizard') === 'true') {
      // Remove the param from URL to avoid re-opening on refresh
      searchParams.delete('openWizard');
      setSearchParams(searchParams, { replace: true });

      if (totalCredits <= 0) {
        setIsInsufficientCreditsOpen(true);
      } else {
        setIsWizardOpen(true);
      }
    }
  }, [searchParams, setSearchParams, totalCredits]);

  // Create analysis request mutation
  const createRequestMutation = useMutation({
    mutationFn: (formData: AnalysisFormData) => requestsService.createRequest(formData),
    onSuccess: (response) => {
      setIsWizardOpen(false);
      // Navigate to analysis results page with correlationId
      navigate(`/analysis/${response.correlationId}`);
    },
    onError: (error) => {
      setSubmitError(
        error instanceof Error ? error.message : t('newAnalysis.failedToCreate')
      );
    },
  });

  const handleOpenWizard = () => {
    if (totalCredits <= 0) {
      setIsInsufficientCreditsOpen(true);
      return;
    }
    setSubmitError(null);
    setIsWizardOpen(true);
  };

  const handleBuyCredits = () => {
    setIsInsufficientCreditsOpen(false);
    setIsBuyCreditsOpen(true);
  };

  const handleCloseWizard = () => {
    setIsWizardOpen(false);
    setSubmitError(null);
  };

  const handleSubmit = (formData: AnalysisFormData) => {
    createRequestMutation.mutate(formData);
  };

  return (
    <DashboardLayout
      header={<DashboardHeader />}
      footer={<Footer />}
    >
      {/* Hero Section */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center max-w-5xl mx-auto py-8 lg:py-12">
        {/* Left: Text Content */}
        <div className="space-y-6">
          <h1 className="gradient-text text-3xl md:text-4xl lg:text-[2.75rem] font-bold leading-tight">
            {t('newAnalysis.heroTitle')}
          </h1>
          <p className="text-base md:text-lg text-[var(--color-charcoal)]/70 leading-relaxed">
            {t('newAnalysis.heroDescription')}
          </p>

          {/* Error alert */}
          {submitError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          )}

          {/* CTA Button */}
          <div className="flex flex-wrap items-center gap-4">
            <Button
              onClick={handleOpenWizard}
              className="btn-primary text-base md:text-lg px-8 py-3 rounded-full"
              disabled={createRequestMutation.isPending}
            >
              {t('newAnalysis.ctaButton')}
            </Button>
          </div>
        </div>

        {/* Right: Hero Illustration */}
        <div className="flex justify-center lg:justify-end">
          <img
            src={newAnalysisHero}
            alt={t('newAnalysis.heroImageAlt')}
            className="w-full max-w-xl lg:max-w-none object-contain rounded-2xl shadow-lg"
          />
        </div>
      </section>

      {/* Analysis dialog */}
      <AnalysisDialog
        isOpen={isWizardOpen}
        onClose={handleCloseWizard}
        onSubmit={handleSubmit}
        isSubmitting={createRequestMutation.isPending}
      />

      {/* Insufficient credits dialog */}
      <InsufficientCreditsDialog
        isOpen={isInsufficientCreditsOpen}
        onClose={() => setIsInsufficientCreditsOpen(false)}
        onBuyCredits={handleBuyCredits}
        currentCredits={totalCredits}
      />

      {/* Buy credits dialog (opened from insufficient credits dialog) */}
      <BuyCreditsDialog
        isOpen={isBuyCreditsOpen}
        onClose={() => setIsBuyCreditsOpen(false)}
      />
    </DashboardLayout>
  );
}
